import type { EventBus } from "../core/EventBus.ts";
import { COLORS } from "../core/constants.ts";
import type { BreachResult } from "../systems/BreachSystem.ts";
import type { Vec2 } from "../core/types.ts";
import { t } from "../utils/i18n.ts";

/**
 * CR-29: BREACH cinematic — 2.5s pre-spawn sequence with timed beats.
 * DD-14.7-5: Existing enemies keep moving during cinematic (no freeze).
 * Listens breach.armed, emits breach.completed when done.
 */
export class BreachCinematic {
  private bus: EventBus;
  private active = false;
  private timer = 0;
  private result: BreachResult | null = null;
  /** GA-42b: latches the one-shot spawn-node beat (see `update`). */
  private spawnBeatFired = false;

  constructor(bus: EventBus) {
    this.bus = bus;
  }

  /** Start the breach cinematic */
  start(result: BreachResult): void {
    this.result = result;
    this.active = true;
    this.timer = 0;
    this.spawnBeatFired = false;

    // T-2.5s: Banner + red vignette + shake
    this.bus.emit("banner.show", {
      text: t("breach.initiated"),
      color: COLORS.ORACLE_RED,
      duration: 2500,
    });
    this.bus.emit("screen.shake", { intensity: 3, duration: 2500 });
    this.bus.emit("screen.vignette", { color: COLORS.ORACLE_RED, duration: 2500 });
    this.bus.emit("audio.play", { profile: "breach_alarm" });
  }

  /** Update each frame. Returns true while cinematic is active. */
  update(dt: number): boolean {
    if (!this.active || !this.result) return false;

    // GA-42: `dt` ALREADY arrives in milliseconds — SimStep.ts:96 passes the
    // same dt it gives combatSystem.update. The old `dt * 1000` (commented
    // "convert to ms") assumed seconds and advanced the timer 1000x too fast,
    // so the whole 2.5 s sequence collapsed into a single frame: no spawn-node
    // beat, no strobe, no telegraphing. Every other dt conversion in src/
    // DIVIDES by 1000 — this was the only site multiplying.
    this.timer += dt;

    // T-1.5s: Spawn node boots up.
    // GA-42b: fire ONCE via an explicit latch, not a `timer < 1000 + dt + 50`
    // window. That window only ever matched a single frame because the broken
    // GA-42 arithmetic made the timer leap 16,670 ms per frame and skip past
    // it; with correct millisecond stepping, four consecutive 60 FPS frames
    // land inside it and the beat fired four times — four `CommsDirector`
    // announcements and four render pulses per breach. A latch is frame-rate
    // independent, so it cannot regress at a different dt.
    if (!this.spawnBeatFired && this.timer >= 1000) {
      this.spawnBeatFired = true;
      this.bus.emit("breach.spawnActivating", {
        col: this.result.spawn.col,
        row: this.result.spawn.row,
      });
    }

    // T-1.0s to T-0.5s: Tower strobe phase (cells highlighted in red)
    if (this.timer >= 1500 && this.timer < 2000) {
      // Strobe is handled by renderer via breach state
    }

    // T-0.5s: Tower destruction is now deferred to PathRevealAnimation (CR 2.1).
    // The cinematic only shows the warning/strobe, actual destruction happens
    // during the progressive path reveal animation.

    // T-0: Cinematic complete
    if (this.timer >= 2500) {
      this.active = false;
      this.bus.emit("breach.completed", {
        spawn: this.result.spawn,
        path: this.result.path,
        destroyedCells: this.result.destroyedCells,
      });
      this.result = null;
      return false;
    }

    return true;
  }

  isActive(): boolean {
    return this.active;
  }

  /** Get cells currently in strobe phase (for renderer) */
  getStrobeCells(): Vec2[] {
    if (!this.active || !this.result) return [];
    if (this.timer >= 1500 && this.timer < 2500) {
      return this.result.destroyedCells;
    }
    return [];
  }
}
